import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import axios from "axios";
import { loginRequest } from "@/config/authConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("tmcteletech_token"));
  const [loading, setLoading] = useState(true);
  const [authMethod, setAuthMethod] = useState(localStorage.getItem("tmcteletech_auth_method"));

  // Impersonation state — when an admin impersonates a user, the admin's
  // original token is stashed under `impersonator_token` so we can
  // "return to admin" without a full re-login. `isImpersonating` is a
  // convenience flag derived from the current token payload.
  const [impersonatorToken, setImpersonatorToken] = useState(
    sessionStorage.getItem("tmcteletech_impersonator_token")
  );
  const [isImpersonating, setIsImpersonating] = useState(
    !!sessionStorage.getItem("tmcteletech_impersonator_token")
  );

  // Set axios auth header when token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, [token]);

  // Global 401 handler — a single expired/invalid token should send the
  // user to /login instead of leaving the dashboard in a half-loaded
  // blank state. This is what caused the "why is my page blank?" reports
  // on 2026-07-02 when a burst of 401s came back mid-render.
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || "";
        // Only bounce on auth-required routes. Ignore 401s on the login
        // endpoints themselves (LoginPage handles those inline).
        const isAuthEndpoint =
          url.includes("/auth/patient/login") ||
          url.includes("/auth/staff/login") ||
          url.includes("/auth/sso/login");
        if (status === 401 && !isAuthEndpoint) {
          // Special case: if we're impersonating and the impersonation
          // token expired, restore the admin token instead of bouncing
          // to /login. This makes the 30-min TTL invisible to admins.
          const stashed = sessionStorage.getItem("tmcteletech_impersonator_token");
          if (stashed) {
            localStorage.setItem("tmcteletech_token", stashed);
            sessionStorage.removeItem("tmcteletech_impersonator_token");
            axios.defaults.headers.common["Authorization"] = `Bearer ${stashed}`;
            setToken(stashed);
            setImpersonatorToken(null);
            setIsImpersonating(false);
            // Notify + reload to home. The admin can start again if needed.
            if (typeof window !== "undefined") {
              window.location.replace("/?impersonation=expired");
            }
            return Promise.reject(error);
          }
          const hadToken = !!localStorage.getItem("tmcteletech_token");
          localStorage.removeItem("tmcteletech_token");
          localStorage.removeItem("tmcteletech_auth_method");
          delete axios.defaults.headers.common["Authorization"];
          setToken(null);
          setUser(null);
          if (hadToken && window.location.pathname !== "/login") {
            // Preserve the target so we can return post-login.
            const next = encodeURIComponent(
              window.location.pathname + window.location.search
            );
            window.location.replace(`/login?next=${next}&reason=expired`);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // Check for existing local auth session on mount
  useEffect(() => {
    const storedToken = localStorage.getItem("tmcteletech_token");
    const storedAuthMethod = localStorage.getItem("tmcteletech_auth_method");
    
    if (storedToken && storedAuthMethod === "local") {
      setToken(storedToken);
      setAuthMethod("local");
      fetchLocalUser(storedToken);
    } else if (storedToken && storedAuthMethod === "sso") {
      setToken(storedToken);
      setAuthMethod("sso");
      fetchLocalUser(storedToken);
    } else {
      setLoading(false);
    }
  }, []);

  // Handle SSO authentication when MSAL completes
  useEffect(() => {
    const handleSSOAuth = async () => {
      if (isAuthenticated && accounts.length > 0 && inProgress === InteractionStatus.None && !user) {
        const account = accounts[0];
        // Check if we already have a valid session
        const existingMethod = localStorage.getItem("tmcteletech_auth_method");
        if (existingMethod === "sso" && localStorage.getItem("tmcteletech_token")) {
          return; // Already authenticated
        }
        await processSSOLogin(account);
      }
    };
    
    handleSSOAuth();
  }, [isAuthenticated, accounts, inProgress, user]);

  const fetchLocalUser = async (authToken) => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error("Failed to fetch user:", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refetch the current user from /auth/me and update the cached user object.
   * Call this after any backend write that changes the user (notification
   * settings, role changes, profile updates, etc.) so the rest of the app
   * sees the new values immediately.
   * Returns the refreshed user, or null on failure.
   */
  const refreshUser = useCallback(async () => {
    if (!token) return null;
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error("refreshUser failed:", error);
      return null;
    }
  }, [token]);

  const processSSOLogin = async (account) => {
    try {
      setLoading(true);
      
      // Get tokens from MSAL
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account,
      });

      const idTokenClaims = account.idTokenClaims || {};
      const roles = idTokenClaims.roles || [];

      console.log("SSO Login - Sending to backend:", {
        email: idTokenClaims.email || idTokenClaims.preferred_username || account.username,
        name: idTokenClaims.name || account.name || "User",
        oid: idTokenClaims.oid || account.localAccountId,
        roles: roles,
      });

      // Send to backend
      const response = await axios.post(`${API}/auth/sso/login`, {
        access_token: tokenResponse.accessToken,
        id_token: tokenResponse.idToken || "",
        email: idTokenClaims.email || idTokenClaims.preferred_username || account.username,
        name: idTokenClaims.name || account.name || "User",
        oid: idTokenClaims.oid || account.localAccountId,
        roles: roles,
      });

      console.log("SSO Login - Backend response:", response.data);

      const { access_token, user: userData } = response.data;
      
      localStorage.setItem("tmcteletech_token", access_token);
      localStorage.setItem("tmcteletech_auth_method", "sso");
      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser(userData);
      setAuthMethod("sso");
      
      console.log("SSO Login - Success, user roles:", userData.roles);
      
    } catch (error) {
      console.error("SSO login failed:", error);
      console.error("SSO login error response:", error.response?.data);
      console.error("SSO login error status:", error.response?.status);
      // Clear any partial state
      localStorage.removeItem("tmcteletech_token");
      localStorage.removeItem("tmcteletech_auth_method");
      delete axios.defaults.headers.common["Authorization"];
      setUser(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  // Local patient login
  const loginPatient = async (email, password) => {
    const response = await axios.post(`${API}/auth/patient/login`, {
      email,
      password,
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("tmcteletech_token", access_token);
    localStorage.setItem("tmcteletech_auth_method", "local");
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setAuthMethod("local");
    return userData;
  };

  // Local staff login (fallback for development)
  const loginStaff = async (email, password) => {
    const response = await axios.post(`${API}/auth/staff/login`, {
      email,
      password,
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("tmcteletech_token", access_token);
    localStorage.setItem("tmcteletech_auth_method", "local");
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setAuthMethod("local");
    return userData;
  };

  // SSO login trigger - use redirect for better reliability
  const loginWithSSO = async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error("SSO login error:", error);
      throw error;
    }
  };

  // Patient registration
  const registerPatient = async (data) => {
    const response = await axios.post(`${API}/auth/patient/register`, data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem("tmcteletech_token", access_token);
    localStorage.setItem("tmcteletech_auth_method", "local");
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setAuthMethod("local");
    return userData;
  };

  // Logout
  const logout = useCallback(() => {
    localStorage.removeItem("tmcteletech_token");
    localStorage.removeItem("tmcteletech_auth_method");
    // Impersonation is a subset of a session — kill it too on logout.
    sessionStorage.removeItem("tmcteletech_impersonator_token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    setImpersonatorToken(null);
    setIsImpersonating(false);
    
    // If SSO, also logout from Microsoft
    if (authMethod === "sso" && accounts.length > 0) {
      instance.logoutRedirect({
        postLogoutRedirectUri: "/login",
      });
    }
    
    setAuthMethod(null);
  }, [authMethod, accounts, instance]);

  /**
   * Admin action: swap the current admin token for a short-lived
   * impersonation token targeting `targetUserId`. The admin's original
   * token is stashed in sessionStorage so `stopImpersonation()` can
   * restore it without a full re-login.
   */
  const startImpersonation = useCallback(
    async (targetUserId) => {
      if (!token) throw new Error("Not authenticated");
      const response = await axios.post(
        `${API}/admin/impersonate/${targetUserId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { access_token, target } = response.data;

      // Stash the original admin token so we can restore it.
      sessionStorage.setItem("tmcteletech_impersonator_token", token);
      // Swap active token to the impersonation JWT.
      localStorage.setItem("tmcteletech_token", access_token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
      setImpersonatorToken(token);
      setIsImpersonating(true);
      setToken(access_token);

      // Fetch the target user so the app renders as them.
      const meResp = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${access_token}` },
      });
      setUser(meResp.data);

      // Route to the target's natural home page.
      return target;
    },
    [token]
  );

  /**
   * Restore the admin token that was stashed when impersonation started
   * and log the stop event on the backend.
   */
  const stopImpersonation = useCallback(async () => {
    const stashed = sessionStorage.getItem("tmcteletech_impersonator_token");
    if (!stashed) {
      // Defensive — nothing to restore. Just log the user out to be safe.
      logout();
      return;
    }
    // Restore admin token FIRST so the audit call is made as the admin.
    localStorage.setItem("tmcteletech_token", stashed);
    sessionStorage.removeItem("tmcteletech_impersonator_token");
    axios.defaults.headers.common["Authorization"] = `Bearer ${stashed}`;
    setImpersonatorToken(null);
    setIsImpersonating(false);
    setToken(stashed);

    // Best-effort audit log — don't block the return if it fails.
    try {
      await axios.post(
        `${API}/admin/impersonate/stop`,
        {},
        { headers: { Authorization: `Bearer ${stashed}` } }
      );
    } catch (err) {
      console.warn("Failed to log impersonation stop:", err);
    }

    // Refetch the admin user so UI switches back.
    try {
      const meResp = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${stashed}` },
      });
      setUser(meResp.data);
    } catch (err) {
      console.error("Failed to refetch admin user after impersonation stop:", err);
      logout();
    }
  }, [logout]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        authMethod,
        loginPatient,
        loginStaff,
        loginWithSSO,
        registerPatient,
        logout,
        refreshUser,
        isAuthenticated: !!user,
        // Impersonation
        isImpersonating,
        impersonatorToken,
        startImpersonation,
        stopImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
